/**
 * SpacesWorkConfig — typed replacement for deploy/public/json/spaces.json.
 *
 * The SPACES department work carousel (physical/installation projects): feel
 * settings plus four project tiles and their floating particle layers. See
 * ScreensWorkConfig for the field conventions ('preset' picks a canned
 * big/small particle behaviour in WorkTilePixi).
 */
import type { ProjectConfig } from 'app/model/ProjectConfig';
import { HeaderType } from 'app/views/project/project.types';
import type { WorkConfig } from 'app/model/WorkConfig';

/**
 * Ready Player One — HTC Vive VR activation. Key-and-crystal particles around
 * the tile.
 */
export const readyPlayerOne: ProjectConfig = {
	order: 1,
	slug: 'ready-player-one',
	image: 'ready-player-one.jpg',
	tint: '0xe169ff',
	particles: [
		// rpo-character
		{
			texture: 'rpo-character.png',
			x: 0.45,
			y: 0.8,
			z: 3.5,
			depth: -2,
			scale: 0.75,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.3,
			projectScale: 1,
			projectY: 0.9,
			mobileProjectY: 0.78,
			projectMobileScale: 1.2
		},
		// rpo-left-pipe
		{
			texture: 'rpo-left-pipe.png',
			x: 0.02,
			y: 0.13,
			z: 1,
			depth: -2,
			scale: 0.6,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.1,
			projectScale: 0.5,
			projectMobileScale: 0.7
		},
		// rpo-right-pipe
		{
			texture: 'rpo-right-pipe.png',
			x: 0.9,
			mobileX: 0.92,
			y: 0.25,
			mobileY: 0.23,
			z: 0.5,
			depth: -2,
			scale: 0.9,
			mobileScale: 0.8,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.1,
			projectScale: 1.1,
			projectMobileScale: 1.1,
			projectX: 0.95
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: -0.025,
			y: 0.25,
			z: 5,
			alpha: 0.1,
			depth: 1,
			scale: 0.6,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: -0.035,
			y: 0.7,
			z: 5,
			alpha: 0.1,
			depth: 1,
			scale: 0.45,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 0.75,
			y: 1.03,
			z: 7,
			depth: 1,
			alpha: 0.07,
			scale: 1.3,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 0.8,
			y: 1.0,
			z: 5,
			depth: 1,
			alpha: 0.13,
			scale: 0.9,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 0.99,
			y: 0.2,
			z: 5,
			depth: 1,
			alpha: 0.13,
			scale: 0.9,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 1,
			y: 1,
			z: 5,
			depth: 1,
			alpha: 0.03,
			scale: 1.4,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-1
		{
			texture: 'rpo-dust-1.png',
			x: 0.08,
			y: 0.46,
			z: 5,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-2
		{
			texture: 'rpo-dust-2.png',
			x: 0.4,
			y: -0.02,
			z: 4.4,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-3
		{
			texture: 'rpo-dust-3.png',
			x: -0.07,
			y: 0.38,
			z: 4.8,
			depth: 1,
			scale: 0.8,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-4
		{
			texture: 'rpo-dust-4.png',
			x: 0.2,
			y: 0.75,
			z: 3,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-5
		{
			texture: 'rpo-dust-5.png',
			x: 1.02,
			y: 0.05,
			z: 2,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-6
		{
			texture: 'rpo-dust-6.png',
			x: 0.95,
			y: 0.76,
			z: 6,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		}
	]
};

/**
 * Anish Kapoor: Into Yourself, Fall — VR artwork. Single ambience particle;
 * the tile itself carries the artwork.
 */
export const anishKapoorIntoYourselfFall: ProjectConfig = {
	order: 2,
	slug: 'anish-kapoor-into-yourself-fall',
	image: 'anush-kapoor.jpg',
	tint: '0x990000',
	particles: [
		// iuf-person
		{
			texture: 'iuf-person.png',
			x: 0.41,
			mobileProjectX: 0.36,
			y: 0.45,
			z: 3,
			depth: 1,
			scale: 0.75,
			retainInCaseStudy: true
		}
	]
};

/**
 * Tracing the Sky — planetarium/sky installation. Video header. Star and
 * trail particles with case-study scroll parallax.
 */
export const tracingTheSky: ProjectConfig = {
	order: 3,
	slug: 'tracing-the-sky',
	image: 'tracing-the-sky.jpg',
	headerType: HeaderType.VIDEO,
	tint: '0x990000',
	particles: [
		// mm-6
		{
			texture: 'mm-6.png',
			x: -0.25,
			y: -1.05,
			speed: 1,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: 1.3,
			y: -0.8,
			speed: 0.6,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: 1.05,
			y: -0.2,
			speed: 0.1,
			depth: -1,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: 1.2,
			y: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -0.3,
			y: 1.0,
			speed: 0.5,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -1.15,
			y: 0.75,
			speed: 0.3,
			depth: -1,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -1.2,
			y: 0,
			speed: 0.6,
			depth: -1,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -1.25,
			y: -0.7,
			speed: 0.2,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -0.5,
			y: 0.3,
			speed: 0.4,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		}
	]
};

/**
 * Tend — DISABLED (`active: false` — WorkController filters it out of the
 * carousel). Kept for parity and possible re-activation.
 */
export const tend: ProjectConfig = {
	order: 4,
	slug: 'tend',
	image: 'tend.jpg',
	tint: '0x990000',
	active: false,
	particles: [
		// mm-6
		{
			texture: 'mm-6.png',
			x: -0.25,
			y: -1.05,
			speed: 1,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: 1.3,
			y: -0.8,
			speed: 0.6,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: 1.05,
			y: -0.2,
			speed: 0.1,
			depth: -1,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: 1.2,
			y: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -0.3,
			y: 1.0,
			speed: 0.5,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -1.15,
			y: 0.75,
			speed: 0.3,
			depth: -1,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -1.2,
			y: 0,
			speed: 0.6,
			depth: -1,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -1.25,
			y: -0.7,
			speed: 0.2,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		},
		// mm-6
		{
			texture: 'mm-6.png',
			x: -0.5,
			y: 0.3,
			speed: 0.4,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyScrollParallax: 0.12,
			autoScale: true,
			preset: ['big', 'small'],
			autoRotate: true,
			autoAlpha: true
		}
	]
};

/**
 * The assembled department config, in the ORIGINAL JSON array order.
 * WorkController.setJSON filters (`active !== false`) and sorts by `order`
 * before use, but the source order is preserved here for parity.
 */
export const SpacesWorkConfig: WorkConfig = {
	dragEasePower: 2, //Ease power while dragging
	easePower: 18, //Ease power while settling
	velocityFactor: 2.5, //Multiplies timeline movement into the drag/slide rotation effect
	velocityFriction: 0.9, //Per-frame falloff pulling the rotation back to 0
	rotationFactor: 45, //Base level of rotation (degrees)
	maxRotation: 35, //Max rotation allowed either way (can exceed base)
	dragToMoveRatio: 1, //Drag distance -> panel movement ratio (1 = 1:1)
	projects: [readyPlayerOne, anishKapoorIntoYourselfFall, tracingTheSky, tend]
};
